<template>
  <div class="app-subscription-order" v-loading="dataLoading">

    <!-- 筛选栏 -->
    <div class="toolbar">
      <div class="filter-container">
        <el-button type="primary" size="small" style="margin-right: auto;" @click="openManualGrantDialog">Manual Grant</el-button>

        <el-date-picker
          v-model="dateRange"
          type="daterange"
          size="small"
          range-separator="~"
          start-placeholder="Start date"
          end-placeholder="End date"
          value-format="timestamp"
          style="width: 240px; margin-right: 15px;"
          @change="handleSearch"
        />
        <div class="search-bar">
          <el-input size="small" v-model="listQuery.q" placeholder="Nickname / Phone No" @keyup.enter.native="handleSearch">
            <el-select v-model="listQuery.queryType" slot="prepend">
              <el-option label="User" value="user"></el-option>
            </el-select>
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </div>
      </div>
    </div>

    <!-- 订单表格 -->
    <el-table :data="tableData" row-key="id" >
      <el-table-column prop="id" label="ID" width="65" />
      <el-table-column prop="nickname" label="Nickname" min-width="120" />
      <el-table-column prop="phoneNo" label="Phone No" min-width="130" />
      <el-table-column prop="planName" label="Plan Name" min-width="120" />
      <el-table-column label="Out Trade No" min-width="210">
        <template slot-scope="scope">
          <span class="trade-no">{{ scope.row.outTradeNo }}</span>
        </template>
      </el-table-column>

      <el-table-column prop="targetLevel" label="Level" width="90" />
      <el-table-column prop="purchaseAction" label="Action" width="85" align="center" />
      <el-table-column label="Original Price" width="115" align="right">
        <template slot-scope="scope">
          ¥{{ (scope.row.originalPriceCents / 100).toFixed(2) }}
        </template>
      </el-table-column>
      <el-table-column label="Actual Price" width="105" align="right">
        <template slot-scope="scope">
          ¥{{ (scope.row.actualPriceCents / 100).toFixed(2) }}
        </template>
      </el-table-column>
      <el-table-column prop="priceSource" label="Price Src" width="95" align="center" />
      <el-table-column prop="durationUnit" label="Period" width="80" align="center" />
      <el-table-column prop="durationValue" label="Duration" width="75" align="center" />

      <el-table-column label="Channel" width="110">
        <template slot-scope="scope">
          <el-tag size="mini" :type="channelTagType(scope.row.payChannel)">
            {{ scope.row.payChannel }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Status" width="95" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="statusTagType(scope.row.status)">
            {{ scope.row.status }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Env" width="55" align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.env === 0 ? 'Prod' : 'Dev' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Paid At" min-width="170">
        <template slot-scope="scope">
          <span v-if="scope.row.paidAt">
            <i class="el-icon-time" />
            <span style="margin-left: 6px">{{ scope.row.paidAt | displayTime() }}</span>
          </span>
          <span v-else class="text-muted">—</span>
        </template>
      </el-table-column>
      <el-table-column label="Created At" min-width="170">
        <template slot-scope="scope">
          <i class="el-icon-time" />
          <span style="margin-left: 6px">{{ scope.row.createdAt | displayTime() }}</span>
        </template>
      </el-table-column>
    </el-table>

    <pagination
      v-show="total > 0"
      :total="total"
      :offset.sync="listQuery.offset"
      :limit.sync="listQuery.limit"
      layout="sizes, prev, pager, next, total"
      @pagination="fetchData"
    />

    <!-- Manual Grant Dialog -->
    <el-dialog title="Manual Grant Subscription" :visible.sync="manualGrantVisible" width="650px" @close="resetManualGrantForm">
      <el-form ref="manualGrantForm" :model="manualGrantForm" :rules="manualGrantRules" label-width="120px" size="small">
        <el-form-item label="Select Users" prop="appUserIds">
          <app-user-selection-multi ref="userSelection" :ids="manualGrantForm.appUserIds" :no-pre-login="true" @select="handleUserSelect" />
        </el-form-item>
        <el-form-item label="Plan" prop="productId">
          <el-select v-model="manualGrantForm.productId" placeholder="Select a plan" style="width: 100%">
            <el-option
              v-for="plan in planOptions"
              :key="plan.productId"
              :label="plan.name + ' (' + plan.productId + ')'"
              :value="plan.productId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Duration Unit" prop="durationUnit">
          <el-select v-model="manualGrantForm.durationUnit" placeholder="Select unit" style="width: 100%">
            <el-option label="Day" value="day" />
            <el-option label="Month" value="month" />
            <el-option label="Year" value="year" />
          </el-select>
        </el-form-item>
        <el-form-item label="Duration Value" prop="durationValue">
          <el-input-number v-model="manualGrantForm.durationValue" :min="1" style="width: 150px;" />
        </el-form-item>
        <el-form-item label="Reason" prop="reason">
          <el-input type="textarea" v-model="manualGrantForm.reason" :rows="3" placeholder="10-500 characters" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="manualGrantVisible = false">Cancel</el-button>
        <el-button type="primary" size="small" :loading="manualGrantSaving" @click="submitManualGrant">Grant</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { fetchAppSubscriptionOrders, manualGrantAppSubscription, fetchAppSubscriptionPlans } from "@/api/admin";
import Pagination from "@/components/Pagination";
import AppUserSelectionMulti from "@/components/admin/AppUserSelectionMulti.vue";

export default {
  name: "AppTabSubscriptionOrder",
  components: { Pagination, AppUserSelectionMulti },
  data() {
    return {
      tableData: [],
      total: 0,
      dataLoading: false,
      dateRange: null,
      listQuery: {
        q: "",
        queryType: "user",
        startTime: undefined,
        endTime: undefined,
        offset: 0,
        limit: 10,
      },
      planOptions: [],
      manualGrantVisible: false,
      manualGrantSaving: false,
      manualGrantForm: {
        appUserIds: [],
        productId: "",
        durationUnit: "day",
        durationValue: 1,
        reason: "",
      },
      manualGrantRules: {
        appUserIds: [{ type: "array", required: true, message: "Please select at least one user", trigger: "change" }],
        productId: [{ required: true, message: "Please select a plan", trigger: "change" }],
        durationUnit: [{ required: true, message: "Duration unit is required", trigger: "change" }],
        durationValue: [{ required: true, message: "Duration value is required", trigger: "blur" }],
        reason: [
          { required: true, message: "Reason is required", trigger: "blur" },
          { min: 10, max: 500, message: "Length should be 10 to 500", trigger: "blur" }
        ],
      },
    };
  },
  mounted() {
    this.active();
  },
  methods: {
    active() {
      this.fetchData();
      this.fetchPlanOptions();
    },
    fetchPlanOptions() {
      fetchAppSubscriptionPlans().then((response) => {
        if (response.success) {
          const items = response.result.items || [];
          this.planOptions = items.filter(plan => !plan.productId.endsWith('_year') && !plan.productId.endsWith('_basic'));
        }
      });
    },
    fetchData() {
      this.dataLoading = true;
      const params = { ...this.listQuery };
      // 去掉空值，避免传 undefined 给后端
      if (!params.q) delete params.q;
      if (!params.startTime) delete params.startTime;
      if (!params.endTime) delete params.endTime;

      fetchAppSubscriptionOrders(params)
        .then((response) => {
          if (response.success) {
            this.tableData = response.result.items || [];
            this.total = response.result.totalCount;
          }
        })
        .finally(() => {
          this.dataLoading = false;
        });
    },
    handleSearch() {
      this.listQuery.offset = 0;
      if (this.dateRange && this.dateRange.length === 2) {
        this.listQuery.startTime = this.dateRange[0];
        // endTime 取当天结束（23:59:59.999）
        this.listQuery.endTime = this.dateRange[1] + 86400000 - 1;
      } else {
        this.listQuery.startTime = undefined;
        this.listQuery.endTime = undefined;
      }
      this.fetchData();
    },
    handleReset() {
      this.dateRange = null;
      this.listQuery = {
        q: "",
        queryType: "user",
        startTime: undefined,
        endTime: undefined,
        offset: 0,
        limit: 10,
      };
      this.fetchData();
    },
    handleUserSelect(ids) {
      this.manualGrantForm.appUserIds = ids;
    },
    openManualGrantDialog() {
      this.manualGrantForm = {
        appUserIds: [],
        productId: "",
        durationUnit: "day",
        durationValue: 1,
        reason: "",
      };
      this.manualGrantVisible = true;
      this.$nextTick(() => {
        this.$refs.manualGrantForm && this.$refs.manualGrantForm.clearValidate();
        this.$refs.userSelection && this.$refs.userSelection.reopen();
      });
    },
    resetManualGrantForm() {
      this.$refs.manualGrantForm && this.$refs.manualGrantForm.resetFields();
    },
    submitManualGrant() {
      this.$refs.manualGrantForm.validate((valid) => {
        if (!valid) return;

        this.manualGrantSaving = true;
        
        Promise.all(this.manualGrantForm.appUserIds.map(userId => {
          return manualGrantAppSubscription({
            appUserId: userId,
            productId: this.manualGrantForm.productId,
            durationUnit: this.manualGrantForm.durationUnit,
            durationValue: this.manualGrantForm.durationValue,
            reason: this.manualGrantForm.reason,
          });
        }))
          .then(() => {
            this.$notify({
              title: "Grant successful",
              type: "success",
            });
            this.manualGrantVisible = false;
            this.fetchData();
          })
          .catch(() => {
            // Errors are caught and displayed globally by request interceptors
          })
          .finally(() => {
            this.manualGrantSaving = false;
          });
      });
    },
    statusTagType(status) {
      const map = {
        delivered: "success",
        paid: "primary",
        pending: "warning",
        refunded: "info",
        failed: "danger",
      };
      return map[status] || "";
    },
    channelTagType(channel) {
      if (!channel) return "";
      if (channel.startsWith("wx")) return "success";
      if (channel.startsWith("alipay")) return "primary";
      if (channel === "manual") return "info";
      return "";
    },
  },
};
</script>

<style lang="scss" scoped>
.app-subscription-order {
  .toolbar {
    margin-bottom: 12px;
    text-align: right;

    .filter-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }

    .search-bar {
      display: inline-block;

      ::v-deep .el-select .el-input {
        width: 100px;
      }

      .el-input {
        width: 350px;
      }
    }
  }

  .trade-no {
    font-size: 12px;
    font-family: monospace;
    color: #606266;
  }

  .text-muted {
    color: #c0c4cc;
  }
}
</style>
